import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MapPin, Plus, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { PURPOSE_OPTIONS, STATUS_COLORS, getDays, calcDuration } from '../components/travel/constants';
import { TravelPlanCard } from '../components/travel/TravelPlanCard';
import { CreatePlanWizard } from '../components/travel/CreatePlanWizard';
import { PlanDetailDialog } from '../components/travel/PlanDetailDialog';
import { CameraDialog } from '../components/travel/CameraDialog';

export default function TravelPlan() {
  const { api, currentUser, selectedBusiness, businessLines } = useApp();
  const [plans, setPlans] = useState([]);
  const [places, setPlaces] = useState([]);
  const [activeTab, setActiveTab] = useState('plans');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailPlan, setDetailPlan] = useState(null);
  const [step, setStep] = useState(1);
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const billInputRef = useRef(null);
  const [form, setForm] = useState({
    visit_type: '', purpose: '', start_date: '', end_date: '',
    locations: [], daily_expenses: [], notes: '',
    mode_of_travel: '', total_distance_km: 0,
  });
  const [locForm, setLocForm] = useState({ business_place_id: '', planned_checkin: '09:00', planned_checkout: '17:00' });

  const load = useCallback(async () => {
    try {
      const params = { user_id: currentUser?.id };
      if (selectedBusiness) params.business_id = selectedBusiness;
      const [pRes, plRes] = await Promise.all([
        api.getTravelPlans(params),
        api.getBusinessPlaces(selectedBusiness ? { business_id: selectedBusiness } : {}),
      ]);
      setPlans(pRes.data.data || pRes.data);
      setPlaces(plRes.data);
    } catch (e) { console.error(e); }
  }, [api, currentUser, selectedBusiness]);

  useEffect(() => { load(); }, [load]);

  const addLocation = () => {
    if (!locForm.business_place_id) { toast.error('Select a location'); return; }
    const place = places.find(p => p.id === locForm.business_place_id);
    const dur = calcDuration(locForm.planned_checkin, locForm.planned_checkout);
    setForm(prev => ({
      ...prev, locations: [...prev.locations, {
        business_place_id: locForm.business_place_id, place_name: place?.name || '', place_city: place?.city || '',
        planned_checkin: locForm.planned_checkin, planned_checkout: locForm.planned_checkout, planned_duration_mins: dur,
      }]
    }));
    setLocForm({ business_place_id: '', planned_checkin: '09:00', planned_checkout: '17:00' });
  };
  const removeLocation = (idx) => setForm(prev => ({ ...prev, locations: prev.locations.filter((_, i) => i !== idx) }));

  const initExpenses = () => {
    const days = getDays(form.start_date, form.end_date);
    const expenses = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(form.start_date); d.setDate(d.getDate() + i);
      expenses.push({ day: i + 1, date: format(d, 'yyyy-MM-dd'), breakfast: 0, lunch: 0, dinner: 0, night_stay: days > 1 && i < days - 1 ? 0 : -1 });
    }
    setForm(prev => ({ ...prev, daily_expenses: expenses }));
  };
  const updateExpense = (idx, field, val) => {
    setForm(prev => ({ ...prev, daily_expenses: prev.daily_expenses.map((e, i) => i === idx ? { ...e, [field]: Number(val) || 0 } : e) }));
  };

  const handleCreate = async () => {
    try {
      await api.createTravelPlan({
        visit_type: form.visit_type, purpose: form.purpose, business_id: selectedBusiness || '',
        start_date: form.start_date, end_date: form.end_date,
        locations: form.locations, daily_expenses: form.daily_expenses, notes: form.notes,
        mode_of_travel: form.mode_of_travel, total_distance_km: form.total_distance_km,
      }, currentUser.id);
      toast.success('Travel plan submitted for approval');
      setCreateOpen(false); setStep(1);
      setForm({ visit_type: '', purpose: '', start_date: '', end_date: '', locations: [], daily_expenses: [], notes: '', mode_of_travel: '', total_distance_km: 0 });
      load();
    } catch (e) { toast.error('Failed to create plan'); }
  };

  const startCamera = async () => {
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch { toast.error('Camera access denied'); setCameraOpen(false); }
  };
  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setCameraOpen(false);
  };
  const captureSelfie = async () => {
    if (!detailPlan) return;
    const video = videoRef.current; if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const selfieData = canvas.toDataURL('image/jpeg', 0.7);
    stopCamera();
    try {
      let lat = 0, lng = 0;
      try {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
        lat = pos.coords.latitude; lng = pos.coords.longitude;
      } catch {}
      await api.startTravelPlan(detailPlan.id, encodeURIComponent(selfieData), lat, lng);
      toast.success('Plan started! Selfie & GPS recorded.');
      load();
      const res = await api.getTravelPlan(detailPlan.id);
      setDetailPlan(res.data);
    } catch (e) { toast.error('Failed to start plan'); }
  };

  const handleCheckin = async (planId, idx) => {
    try {
      let lat = 0, lng = 0;
      try {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
        lat = pos.coords.latitude; lng = pos.coords.longitude;
      } catch {}
      const res = await api.checkinLocation(planId, idx, lat, lng);
      
      // Check validation result
      if (res.data.last_validation && !res.data.last_validation.is_valid) {
        toast.warning(`Check-in recorded but location validation failed: ${res.data.last_validation.distance_meters}m away (max: ${res.data.last_validation.tolerance_meters}m)`);
      } else {
        toast.success('Checked in');
      }
      setDetailPlan(res.data); load();
    }
    catch { toast.error('Check-in failed'); }
  };
  const handleCheckout = async (planId, idx) => {
    try {
      let lat = 0, lng = 0;
      try {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
        lat = pos.coords.latitude; lng = pos.coords.longitude;
      } catch {}
      const res = await api.checkoutLocation(planId, idx, lat, lng);
      
      // Check validation result
      if (res.data.last_validation && !res.data.last_validation.is_valid) {
        toast.warning(`Check-out recorded but location validation failed: ${res.data.last_validation.distance_meters}m away (max: ${res.data.last_validation.tolerance_meters}m)`);
      } else {
        toast.success('Checked out');
      }
      setDetailPlan(res.data); load();
    }
    catch { toast.error('Check-out failed'); }
  };
  const handleComplete = async (planId) => {
    try { await api.completeTravelPlan(planId); toast.success('Plan completed!'); load(); const res = await api.getTravelPlan(planId); setDetailPlan(res.data); }
    catch { toast.error('Failed to complete'); }
  };
  const handleBillUpload = async (planId, e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const bills = [];
    for (const file of files) {
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.readAsDataURL(file);
      });
      bills.push(dataUrl);
    }
    try {
      const res = await api.uploadBills(planId, bills);
      toast.success(`${files.length} bill(s) uploaded`);
      setDetailPlan(res.data); load();
    } catch { toast.error('Upload failed'); }
  };

  const activeBiz = businessLines.find(b => b.id === selectedBusiness);
  const approvedPlans = plans.filter(p => p.status === 'approved');
  const activePlans = plans.filter(p => p.status === 'in_progress');
  const completedPlans = plans.filter(p => p.status === 'completed');

  return (
    <div data-testid="travel-plan-page" className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Travel Plans</h1>
          <p className="text-sm text-slate-500 mt-1">{activeBiz ? activeBiz.label + ' - ' : ''}Create and manage your travel plans</p>
        </div>
        <Button data-testid="create-plan-btn" onClick={() => { setCreateOpen(true); setStep(1); }} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-md">
          <Plus className="w-4 h-4" /> New Travel Plan
        </Button>
      </div>

      <InstructionGuide
        title="How Travel Plans work"
        description="Create travel plans, submit them for approval, then execute plans with GPS check-ins and selfie verification."
        steps={[
          'Click "New Travel Plan" to start the 4-step creation wizard.',
          'Add trip details, locations, and daily expense estimates, then submit for approval.',
          'Once approved, start the plan with a selfie capture and check-in at each location.',
          'After completing all stops, mark the plan as complete and upload expense bills.',
        ]}
        flow={['Create Plan', 'Submit for Approval', 'Start Plan (Selfie)', 'Check-in / Check-out', 'Complete & Upload Bills']}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="plans" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">All Plans</TabsTrigger>
          <TabsTrigger value="action" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Plan of Action</TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Completed</TabsTrigger>
        </TabsList>

        <TabsContent value="plans">
          {plans.length === 0 ? (
            <Card className="border border-border/60"><CardContent className="p-12 text-center">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-400">No travel plans yet. Create your first plan.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{plans.map(p => <TravelPlanCard key={p.id} plan={p} onClick={() => setDetailPlan(p)} />)}</div>
          )}
        </TabsContent>

        <TabsContent value="action">
          <Card className="border border-border/60 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-slate-900">Plan of Action</CardTitle></CardHeader>
            <CardContent>
              {[...approvedPlans, ...activePlans].length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No active plans. Approved plans will appear here.</p>
              ) : (
                <div className="space-y-2">
                  {[...approvedPlans, ...activePlans].map(p => {
                    const purpose = PURPOSE_OPTIONS.find(o => o.value === p.purpose)?.label || p.purpose;
                    return (
                      <div key={p.id} onClick={() => setDetailPlan(p)} className="flex items-center gap-3 p-3 rounded-lg border border-border/40 hover:bg-slate-50 cursor-pointer">
                        <div className={`w-2 h-10 rounded-full ${p.status === 'in_progress' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{purpose}</p>
                          <p className="text-xs text-slate-400">{p.start_date} to {p.end_date} | {(p.locations || []).length} locations</p>
                        </div>
                        <Badge className={`text-[10px] ${STATUS_COLORS[p.status]}`}>{p.status.replace('_', ' ')}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="completed">
          {completedPlans.length === 0 ? (
            <Card className="border border-border/60"><CardContent className="p-12 text-center"><CheckCircle2 className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-400">No completed plans</p></CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{completedPlans.map(p => <TravelPlanCard key={p.id} plan={p} onClick={() => setDetailPlan(p)} />)}</div>
          )}
        </TabsContent>
      </Tabs>

      <CreatePlanWizard
        open={createOpen} onOpenChange={setCreateOpen}
        form={form} setForm={setForm} step={step} setStep={setStep}
        locForm={locForm} setLocForm={setLocForm} places={places}
        addLocation={addLocation} removeLocation={removeLocation}
        initExpenses={initExpenses} updateExpense={updateExpense}
        handleCreate={handleCreate}
      />

      <PlanDetailDialog
        plan={cameraOpen ? null : detailPlan}
        onClose={() => setDetailPlan(null)}
        onCheckin={handleCheckin} onCheckout={handleCheckout}
        onComplete={handleComplete} onBillUpload={handleBillUpload}
        onStartCamera={startCamera} billInputRef={billInputRef}
      />

      <CameraDialog
        open={cameraOpen} onClose={stopCamera}
        videoRef={videoRef} onCapture={captureSelfie}
      />
    </div>
  );
}
